/**
 * 統一された戻り値の型
 */
export type ServiceResponse<T> = {
  data: T | null; // 成功または400時のフローデータ
  error: Error | null; // 予期せぬエラー
  redirectTo?: string; // 外部リダイレクトが必要な場合 (OIDC 等)
  isConflict?: boolean; // HTTP 409 Conflict（唯一の認証手段の解除不可等）
  flowExpired?: boolean; // HTTP 410 Gone（self_service_flow_expired）— 新フローを data に再発行して返したことを示す
  accountDisabled?: boolean; // HTTP 401（identity is disabled）— 利用停止アカウントのログイン拒否を示す
};

/**
 * エラーレスポンスの型定義
 * @ory/clientは内部的にAxiosを使用しているため、エラーオブジェクトはAxiosErrorの形式
 */
export type ErrorResponse = {
  response?: {
    status?: number;
    data?: unknown;
  };
};

/**
 * エラーオブジェクトからHTTPステータスコードを取得
 *
 * @param error - エラーオブジェクト
 * @returns HTTPステータスコード。取得できない場合はnull
 */
function getHttpStatus(error: unknown): number | null {
  if (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status;
  }
  return null;
}

/**
 * エラーが指定されたHTTPステータスコードかどうかを判定
 *
 * @param error - エラーオブジェクト
 * @param status - 判定するHTTPステータスコード
 * @returns 指定されたステータスコードの場合はtrue
 */
export function hasHttpStatus(
  error: unknown,
  status: number,
): error is ErrorResponse {
  return getHttpStatus(error) === status;
}

/**
 * エラーが400 Bad Requestかどうかを判定
 * 400エラーはバリデーションエラーとして扱い、フローデータが含まれている
 */
export function is400Error(error: unknown): error is ErrorResponse {
  return hasHttpStatus(error, 400);
}

/**
 * エラーが401 Unauthorizedかどうかを判定
 * 401エラーは未ログインという正常な状態として扱う
 */
export function is401Error(error: unknown): error is ErrorResponse {
  return hasHttpStatus(error, 401);
}

/**
 * エラーが403 Forbiddenかどうかを判定
 * 403エラーはAALレベル不足などで、未ログインとして扱う
 */
export function is403Error(error: unknown): error is ErrorResponse {
  return hasHttpStatus(error, 403);
}

/**
 * エラーが410 Goneかどうかを判定
 * Ory Kratos は Self-Service Flow が失効した場合に 410 を返す
 */
function is410Error(error: unknown): error is ErrorResponse {
  return hasHttpStatus(error, 410);
}

/**
 * エラーが422 Unprocessable Entityかどうかを判定
 * Ory Kratosは外部リダイレクトが必要な場合 (OIDC等) に422を返す
 */
function is422Error(error: unknown): error is ErrorResponse {
  return hasHttpStatus(error, 422);
}

/**
 * Kratos の self_service_flow_expired (HTTP 410 Gone) かどうかを判定
 *
 * Self-Service Flow（Login / Recovery / Settings 等）の有効期限が切れた場合に
 * Kratos が返す標準エラー。呼び出し側は新規フローを `initFlow()` で作成し直して
 * 継続する必要がある。
 *
 * @example
 * ```ts
 * try {
 *   await updateLoginFlow(...);
 * } catch (err) {
 *   if (isFlowExpired(err)) {
 *     const newFlow = await signInService.initFlow();
 *     return { data: newFlow, flowExpired: true, error: null };
 *   }
 *   throw err;
 * }
 * ```
 */
export function isFlowExpired(error: unknown): error is ErrorResponse {
  if (!is410Error(error)) return false;
  const data = getErrorResponseData<OryKratosErrorResponse>(error);
  return data?.error?.id === 'self_service_flow_expired';
}

/**
 * エラーレスポンスからデータを取得
 */
export function getErrorResponseData<T>(error: ErrorResponse): T | null {
  if (
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    return error.response.data as T;
  }
  return null;
}

/**
 * Ory Kratosのエラーレスポンス型
 * @ory/clientのエラーレスポンスにはerror.idフィールドが含まれる
 */
export type OryKratosErrorResponse = {
  error?: {
    id?: string;
    message?: string;
    code?: number;
    status?: string;
    reason?: string;
    details?: unknown;
  };
};

type BrowserLocationChangeResponse = OryKratosErrorResponse & {
  redirect_browser_to?: string;
};

/**
 * エラーが browser_location_change_required (422) かどうかを判定
 * OIDC フローで外部 IdP へのリダイレクトが必要な場合に Kratos が返す
 */
export function isBrowserLocationChangeRequired(
  error: unknown,
): error is ErrorResponse {
  if (!is422Error(error)) return false;

  const errorData = getErrorResponseData<OryKratosErrorResponse>(error);
  return errorData?.error?.id === 'browser_location_change_required';
}

/**
 * リダイレクト先 URL が安全かどうかを検証する
 *
 * 相対パス（"/" 始まり）または https プロトコルの URL のみ許可し、
 * オープンリダイレクト攻撃を防止する。
 */
function isSafeRedirectUrl(url: string): boolean {
  if (url.startsWith('/')) {
    return true;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * 422 browser_location_change_required レスポンスからリダイレクト先 URL を取得
 *
 * 安全でない URL（http や不正なプロトコル）はブロックし null を返す。
 */
export function getRedirectBrowserTo(error: unknown): string | null {
  if (!is422Error(error)) return null;

  const data = getErrorResponseData<BrowserLocationChangeResponse>(error);
  const url = data?.redirect_browser_to ?? null;

  if (url && !isSafeRedirectUrl(url)) {
    console.warn('Blocked unsafe redirect URL:', url);
    return null;
  }

  return url;
}

/**
 * SSO エラー時に Kratos の FlowError.error フィールドから取り出す詳細情報
 *
 * Webhook が `can_interrupt: true` + `response.parse: true` で失敗した場合に
 * Kratos が返す `GET /self-service/errors?id={error_id}` レスポンスの `error` オブジェクト構造
 */
export type SsoErrorDetail = {
  code?: number;
  status?: string;
  reason?: string;
  message?: string;
};

/**
 * 利用停止（所属組織がすべて無くなる）で identity が `inactive` になると、再ログイン時に
 * Kratos コアが after-login webhook より前に 401 を返す。この 401 は `flow.ui.messages` に
 * 載らず安定した `error.id` も無いため reason / message 文字列で判定する。文言変更リスクに
 * 備え、判定文字列はこの 2 定数のみに集約する。
 */
const ACCOUNT_DISABLED_REASON = 'This account was disabled.';
const ACCOUNT_DISABLED_MESSAGE = 'identity is disabled';

function matchesAccountDisabled(detail: SsoErrorDetail | undefined): boolean {
  if (!detail) return false;
  return (
    detail.reason === ACCOUNT_DISABLED_REASON ||
    detail.message === ACCOUNT_DISABLED_MESSAGE
  );
}

/** 利用停止アカウントの 401（SPA/fetch 経路）を判定する。@see isAccountDisabledDetail */
export function isAccountDisabledError(error: unknown): boolean {
  if (!is401Error(error)) return false;
  const data = getErrorResponseData<OryKratosErrorResponse>(error);
  return matchesAccountDisabled(data?.error);
}

/** 取得済みの FlowError 詳細（ブラウザ redirect 経路）が利用停止アカウントかを判定する。 */
export function isAccountDisabledDetail(
  detail: SsoErrorDetail | null | undefined,
): boolean {
  return matchesAccountDisabled(detail ?? undefined);
}

/**
 * Kratos の flow-interrupting webhook が UiText.context に格納するカスタムフィールド
 *
 * webhook レスポンスの messages[].messages[].context に含まれる独自フィールドを
 * 型安全にアクセスするための型定義。@ory/client の UiText.context は `object` 型の
 * ため、この型を介してアクセスする。
 */
export type WebhookMessageContext = {
  errorCode?: string;
  email?: string;
  provider?: string;
  displayMessages?: Record<string, string>;
};

/**
 * UiText.context から webhook カスタムフィールドを型安全に取得する
 *
 * @param context - UiText.context（@ory/client では `object | undefined`）
 * @returns WebhookMessageContext、または context が存在しない場合は undefined
 */
export function getWebhookMessageContext(
  context: object | undefined,
): WebhookMessageContext | undefined {
  if (!context || typeof context !== 'object') return undefined;
  return context as WebhookMessageContext;
}

const SSO_CREDENTIALS_LINKED_ERROR_CODE = 'SSO_CREDENTIALS_LINKED';

/**
 * UiText が SSO_CREDENTIALS_LINKED webhook メッセージかどうかを判定する
 *
 * SSO 初回ログイン時にバックエンドが招待自動承認 + OIDC credential 追加を行い、
 * Registration Flow を中断して返す info メッセージを検出する。
 */
export function isSsoCredentialsLinkedMessage(msg: {
  type: string;
  context?: object;
}): boolean {
  if (msg.type !== 'info') return false;
  const ctx = getWebhookMessageContext(msg.context);
  return ctx?.errorCode === SSO_CREDENTIALS_LINKED_ERROR_CODE;
}

/**
 * SSO_CREDENTIALS_LINKED メッセージから email を取得する
 *
 * login_hint として使用し、IdP のアカウント選択画面をスキップするために利用。
 */
export function getSsoLinkedEmail(msg: {
  context?: object;
}): string | undefined {
  const ctx = getWebhookMessageContext(msg.context);
  return ctx?.email;
}

/**
 * SSO_CREDENTIALS_LINKED メッセージから OIDC provider を取得する
 *
 * webhook context に provider が含まれていない場合は undefined を返す。
 * 呼び出し側で SUPPORTED_OIDC_PROVIDERS[0] 等のフォールバックを行う。
 */
export function getSsoLinkedProvider(msg: {
  context?: object;
}): string | undefined {
  const ctx = getWebhookMessageContext(msg.context);
  return ctx?.provider;
}
